"use client";

// Declare spline-viewer custom element for JSX
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'spline-viewer': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement> & {
        url?: string;
      }, HTMLElement>;
    }
  }
}

import React, { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  Laptop,
  TrendingUp,
  Palmtree,
  GraduationCap,
  Wrench,
  Rocket,
  ArrowRight,
  MapPin,
  Clock,
  ExternalLink,
  Star,
  Target,
  Crown,
  Users,
  Briefcase,
  Globe
} from "lucide-react";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 }
  }
};

const staggerItem = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

// Job categories
const categories = ["All", "Engineering", "Operations", "Sales", "Marketing"];

// Job listings data
const jobs = [
  {
    id: 1,
    title: "Full Stack Developer",
    location: "Remote",
    type: "Full-time",
    category: "Engineering",
    detailUrl: "/careers/full-stack-developer"
  },
  {
    id: 2,
    title: "Voice AI Engineer",
    location: "Remote",
    type: "Full-time",
    category: "Engineering",
    detailUrl: "/careers/voice-ai-engineer"
  },
  {
    id: 3,
    title: "Customer Success Lead",
    location: "Remote",
    type: "Full-time",
    category: "Operations",
    detailUrl: "/careers/customer-success-lead"
  },
  {
    id: 4,
    title: "Sales Development Rep",
    location: "Remote",
    type: "Full-time",
    category: "Sales",
    detailUrl: "/careers/sales-development-rep"
  },
  {
    id: 5,
    title: "Social Media & Brand Manager",
    location: "Remote (Canada/US)",
    type: "Full-time",
    category: "Marketing",
    detailUrl: "/careers/social-media-brand-manager"
  },
  {
    id: 6,
    title: "Growth Marketing",
    location: "Remote",
    type: "Full-time",
    category: "Marketing",
    detailUrl: "/careers/growth-marketing"
  },
  {
    id: 7,
    title: "Account Executive",
    location: "Remote (US/Canada)",
    type: "Full-time",
    category: "Sales",
    detailUrl: "/careers/account-executive"
  },
  {
    id: 8,
    title: "Integrations Engineer",
    location: "Remote",
    type: "Full-time",
    category: "Engineering",
    detailUrl: "/careers/integrations-engineer"
  }
];

// Benefits data
const benefits = [
  {
    icon: Laptop,
    title: "Remote-first",
    description: "Work from anywhere. We're distributed and async-friendly."
  },
  {
    icon: TrendingUp,
    title: "Equity",
    description: "Real ownership in a company that's actually growing."
  },
  {
    icon: Palmtree,
    title: "Flexible PTO",
    description: "Take the time you need. We trust you."
  },
  {
    icon: GraduationCap,
    title: "Learning budget",
    description: "$1,000/year for courses, books, conferences — whatever helps you grow."
  },
  {
    icon: Wrench,
    title: "Best tools",
    description: "MacBook, software, gear — whatever you need to do your best work."
  },
  {
    icon: Rocket,
    title: "High impact",
    description: "Small team = your work actually matters. Ship things that real people use."
  }
];

// StatItem sub-component for hero
const StatItem = ({ value, label }: { value: string; label: string }) => (
  <div className="flex flex-col items-center justify-center transition-transform hover:-translate-y-1 cursor-default">
    <span className="text-xl font-bold text-white sm:text-2xl">{value}</span>
    <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium sm:text-xs">{label}</span>
  </div>
);

// Glassmorphism Hero Section
const HeroSection = () => {
  const scrollToPositions = () => {
    document.getElementById("positions")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="relative w-full text-white overflow-hidden font-sans">
      {/* Scoped Animations */}
      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes marquee {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes pulse-glow {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }
        .animate-fade-in {
          animation: fadeSlideIn 0.8s ease-out forwards;
          opacity: 0;
        }
        .animate-marquee {
          animation: marquee 40s linear infinite;
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .delay-100 { animation-delay: 0.1s; }
        .delay-200 { animation-delay: 0.2s; }
        .delay-300 { animation-delay: 0.3s; }
        .delay-400 { animation-delay: 0.4s; }
        .delay-500 { animation-delay: 0.5s; }
      `}</style>

      {/* Centered gradient effect for hero only */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(ellipse 80% 50% at 50% 40%, rgba(16, 185, 129, 0.15) 0%, rgba(20, 184, 166, 0.08) 30%, transparent 70%)"
        }}
      />

      <div className="relative z-10 mx-auto max-w-7xl px-4 pt-24 pb-12 sm:px-6 md:pt-32 md:pb-20 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-8 items-start">

          {/* Left Column - Content */}
          <div className="lg:col-span-7 flex flex-col justify-center space-y-8 pt-8">

            {/* Badge */}
            <div className="animate-fade-in delay-100">
              <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/25 bg-emerald-500/10 px-3 py-1.5 backdrop-blur-md transition-colors hover:bg-emerald-500/15">
                <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-emerald-300 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  CAREERS AT AYRA
                </span>
              </div>
            </div>

            {/* Heading */}
            <h1
              className="animate-fade-in delay-200 text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-medium tracking-tighter leading-[0.9]"
              style={{
                maskImage: "linear-gradient(180deg, black 0%, black 80%, transparent 100%)",
                WebkitMaskImage: "linear-gradient(180deg, black 0%, black 80%, transparent 100%)"
              }}
            >
              Build the future of<br />
              <span className="bg-gradient-to-br from-emerald-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent">
                voice AI
              </span><br />
              with us.
            </h1>

            {/* Description */}
            <p className="animate-fade-in delay-300 max-w-xl text-lg text-zinc-400 leading-relaxed">
              We're a small team solving big problems. If you're obsessed with making AI
              actually useful for real businesses, you'll fit right in.
            </p>

            {/* CTA Buttons */}
            <div className="animate-fade-in delay-400 flex flex-col sm:flex-row gap-4">
              <button
                onClick={scrollToPositions}
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-emerald-600 to-teal-600 px-8 py-4 text-sm font-semibold text-white transition-all hover:scale-[1.02] hover:shadow-lg hover:shadow-emerald-500/25 active:scale-[0.98]"
              >
                See Open Positions
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>

              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-2 rounded-full border border-white/10 bg-white/5 px-8 py-4 text-sm font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/10 hover:border-white/20"
              >
                <ExternalLink className="w-4 h-4" />
                Get in Touch
              </Link>
            </div>
          </div>

          {/* Right Column - Voice AI Orb */}
          <div className="col-span-1 lg:col-span-5 relative">
            <div className="relative h-[280px] lg:h-[500px] w-full flex items-center justify-center">
              {/* Premium Voice AI Orb */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                className="relative scale-[0.65] lg:scale-100"
              >
                {/* Outer glow rings */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full border border-emerald-500/5 animate-pulse" style={{ animationDuration: '4s' }} />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[340px] h-[340px] rounded-full border border-emerald-500/10 animate-pulse" style={{ animationDuration: '3s' }} />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[280px] h-[280px] rounded-full border border-emerald-500/15 animate-pulse" style={{ animationDuration: '2.5s' }} />
                
                {/* Main orb container */}
                <div className="relative w-60 h-60">
                  {/* Ambient glow */}
                  <div 
                    className="absolute inset-0 rounded-full blur-3xl opacity-40"
                    style={{ background: 'radial-gradient(circle, rgba(16, 185, 129, 0.4) 0%, rgba(20, 184, 166, 0.2) 50%, transparent 70%)' }}
                  />
                  
                  {/* Orb background */}
                  <div className="absolute inset-4 rounded-full bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 shadow-2xl">
                    {/* Inner gradient overlay */}
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-emerald-500/10 via-transparent to-teal-500/10" />
                    
                    {/* Reflective highlight */}
                    <div className="absolute top-4 left-6 right-6 h-1/3 rounded-full bg-gradient-to-b from-white/10 to-transparent blur-sm" />
                  </div>
                  
                  {/* Animated wave rings */}
                  <svg className="absolute inset-0 w-full h-full" viewBox="0 0 200 200">
                    <defs>
                      <linearGradient id="waveGradientHero" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#10B981" stopOpacity="0.8" />
                        <stop offset="50%" stopColor="#14B8A6" stopOpacity="0.6" />
                        <stop offset="100%" stopColor="#06B6D4" stopOpacity="0.4" />
                      </linearGradient>
                    </defs>
                    
                    {/* Animated circles */}
                    <circle cx="100" cy="100" r="60" fill="none" stroke="url(#waveGradientHero)" strokeWidth="1" opacity="0.6">
                      <animate attributeName="r" values="55;65;55" dur="3s" repeatCount="indefinite" />
                      <animate attributeName="opacity" values="0.6;0.3;0.6" dur="3s" repeatCount="indefinite" />
                    </circle>
                    <circle cx="100" cy="100" r="72" fill="none" stroke="url(#waveGradientHero)" strokeWidth="0.8" opacity="0.4">
                      <animate attributeName="r" values="68;78;68" dur="4s" repeatCount="indefinite" />
                      <animate attributeName="opacity" values="0.4;0.2;0.4" dur="4s" repeatCount="indefinite" />
                    </circle>
                    <circle cx="100" cy="100" r="84" fill="none" stroke="url(#waveGradientHero)" strokeWidth="0.5" opacity="0.3">
                      <animate attributeName="r" values="80;90;80" dur="5s" repeatCount="indefinite" />
                      <animate attributeName="opacity" values="0.3;0.1;0.3" dur="5s" repeatCount="indefinite" />
                    </circle>
                  </svg>
                  
                  {/* Center icon */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <motion.div
                      animate={{ scale: [1, 1.05, 1] }}
                      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                      className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/30"
                    >
                      <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                      </svg>
                    </motion.div>
                  </div>
                </div>
                
                {/* Floating particles */}
                {[...Array(6)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-2 h-2 rounded-full bg-emerald-400/60"
                    style={{
                      top: `${30 + Math.sin(i * 1.2) * 40}%`,
                      left: `${30 + Math.cos(i * 1.2) * 40}%`,
                    }}
                    animate={{
                      y: [0, -10, 0],
                      opacity: [0.4, 0.8, 0.4],
                    }}
                    transition={{
                      duration: 2 + i * 0.5,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: i * 0.3,
                    }}
                  />
                ))}
              </motion.div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

// Culture Section
const CultureSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const values = [
    {
      label: "OUR MISSION",
      number: "01",
      text: "To make AI so good at handling conversations that business owners never miss another opportunity."
    },
    {
      label: "OUR VISION",
      number: "02",
      text: "A world where every small business has enterprise-level customer service — powered by AI that actually works."
    },
    {
      label: "OUR VALUES",
      number: "03",
      text: "Ship fast. Stay scrappy. Obsess over the details. Build things that matter."
    }
  ];

  return (
    <section ref={ref} className="relative py-24 md:py-32 overflow-hidden">
      {/* Subtle grid background */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }}
      />

      <div className="container px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Shield Voice AI Visual */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="relative aspect-square rounded-3xl overflow-hidden hidden lg:flex items-center justify-center"
          >
            {/* Animated radial dot pattern background */}
            <motion.div 
              className="absolute inset-0"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              style={{
                backgroundImage: `radial-gradient(circle at center, rgba(16, 185, 129, 0.2) 1px, transparent 1px)`,
                backgroundSize: '16px 16px',
                maskImage: 'radial-gradient(circle at center, black 0%, transparent 70%)',
                WebkitMaskImage: 'radial-gradient(circle at center, black 0%, transparent 70%)'
              }}
            />
            
            {/* Pulsing outer glow */}
            <motion.div 
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
              animate={{ 
                opacity: [0.3, 0.6, 0.3],
                scale: [1, 1.05, 1]
              }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              style={{
                background: 'radial-gradient(circle at center, rgba(16, 185, 129, 0.15) 0%, transparent 50%)'
              }}
            />
            
            {/* Floating particles */}
            {[...Array(8)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1.5 h-1.5 rounded-full bg-emerald-400"
                style={{
                  top: `${35 + Math.sin(i * 0.8) * 25}%`,
                  left: `${35 + Math.cos(i * 0.8) * 25}%`,
                }}
                animate={{
                  y: [0, -15, 0],
                  x: [0, Math.sin(i) * 5, 0],
                  opacity: [0.2, 0.7, 0.2],
                  scale: [0.8, 1.2, 0.8],
                }}
                transition={{
                  duration: 3 + i * 0.4,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: i * 0.3,
                }}
              />
            ))}
            
            {/* Shield container */}
            <motion.div 
              className="relative"
              animate={{ y: [0, -5, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            >
              {/* Animated glow ring behind shield */}
              <motion.div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 rounded-full"
                animate={{ 
                  scale: [1, 1.1, 1],
                  opacity: [0.3, 0.5, 0.3]
                }}
                transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                style={{
                  background: 'radial-gradient(circle, rgba(16, 185, 129, 0.2) 0%, transparent 70%)'
                }}
              />
              
              {/* Shield SVG */}
              <motion.svg 
                viewBox="0 0 200 240" 
                className="w-48 h-56 md:w-56 md:h-64 relative z-10"
                style={{ filter: 'drop-shadow(0 20px 40px rgba(16, 185, 129, 0.4))' }}
                animate={{ 
                  filter: [
                    'drop-shadow(0 20px 40px rgba(16, 185, 129, 0.3))',
                    'drop-shadow(0 25px 50px rgba(16, 185, 129, 0.5))',
                    'drop-shadow(0 20px 40px rgba(16, 185, 129, 0.3))'
                  ]
                }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              >
                <defs>
                  {/* Shield gradient */}
                  <linearGradient id="shieldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#34D399" stopOpacity="0.95" />
                    <stop offset="50%" stopColor="#10B981" stopOpacity="0.9" />
                    <stop offset="100%" stopColor="#059669" stopOpacity="0.85" />
                  </linearGradient>
                  
                  {/* Inner shadow gradient */}
                  <linearGradient id="shieldInnerShadow" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#000" stopOpacity="0.05" />
                    <stop offset="100%" stopColor="#000" stopOpacity="0.2" />
                  </linearGradient>
                  
                  {/* Highlight gradient */}
                  <linearGradient id="shieldHighlight" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#fff" stopOpacity="0.3" />
                    <stop offset="50%" stopColor="#fff" stopOpacity="0.08" />
                    <stop offset="100%" stopColor="#fff" stopOpacity="0" />
                  </linearGradient>
                </defs>
                
                {/* Shield shape - main body */}
                <path
                  d="M100 10 L180 50 L180 120 C180 170 140 210 100 230 C60 210 20 170 20 120 L20 50 Z"
                  fill="url(#shieldGradient)"
                />
                
                {/* Inner shadow overlay */}
                <path
                  d="M100 10 L180 50 L180 120 C180 170 140 210 100 230 C60 210 20 170 20 120 L20 50 Z"
                  fill="url(#shieldInnerShadow)"
                />
                
                {/* Highlight overlay */}
                <path
                  d="M100 15 L170 52 L170 70 C130 60 70 60 30 70 L30 52 Z"
                  fill="url(#shieldHighlight)"
                />
                
                {/* Inner shield border */}
                <path
                  d="M100 30 L160 60 L160 115 C160 155 130 185 100 200 C70 185 40 155 40 115 L40 60 Z"
                  fill="none"
                  stroke="rgba(255,255,255,0.1)"
                  strokeWidth="1.5"
                />
              </motion.svg>
              
              {/* Mic icon with sound waves */}
              <div className="absolute inset-0 flex items-center justify-center z-20" style={{ paddingBottom: '12px' }}>
                <div className="relative">
                  {/* Animated sound wave rings */}
                  {[1, 2, 3].map((ring) => (
                    <motion.div
                      key={ring}
                      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-emerald-300/30"
                      style={{ width: 60 + ring * 20, height: 60 + ring * 20 }}
                      animate={{
                        scale: [1, 1.3, 1],
                        opacity: [0.4, 0, 0.4],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeOut",
                        delay: ring * 0.4,
                      }}
                    />
                  ))}
                  
                  {/* Mic icon */}
                  <motion.div
                    animate={{ 
                      scale: [1, 1.08, 1],
                    }}
                    transition={{ 
                      duration: 2, 
                      repeat: Infinity, 
                      ease: "easeInOut" 
                    }}
                  >
                    <svg 
                      viewBox="0 0 24 24" 
                      className="w-14 h-14 md:w-16 md:h-16 relative z-10"
                      fill="none" 
                      stroke="#10B981" 
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      {/* Mic body */}
                      <rect x="9" y="2" width="6" height="11" rx="3" fill="rgba(16, 185, 129, 0.2)" />
                      {/* Sound waves */}
                      <path d="M5 10a7 7 0 0 0 14 0" />
                      {/* Stand */}
                      <line x1="12" y1="17" x2="12" y2="21" />
                      <line x1="8" y1="21" x2="16" y2="21" />
                    </svg>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <motion.h2
              variants={staggerItem}
              className="headline text-3xl md:text-4xl lg:text-5xl text-white mb-14"
            >
              We're building AI that gives people their{" "}
              <span className="text-primary">time back</span>.
            </motion.h2>

            <div className="grid gap-6">
              {values.map((item, index) => (
                <motion.div
                  key={index}
                  variants={staggerItem}
                  whileHover={{ x: 8, scale: 1.01 }}
                  transition={{ duration: 0.3 }}
                  className="group relative p-6 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-primary/30 hover:bg-white/[0.04] transition-all duration-300 cursor-default"
                >
                  {/* Number badge */}
                  <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center">
                    <span className="text-[10px] font-bold text-primary">{item.number}</span>
                  </div>

                  <div className="pl-4">
                    <span className="section-label text-primary mb-2 block group-hover:tracking-wider transition-all duration-300">
                      {item.label}
                    </span>
                    <p className="text-white/70 body-text group-hover:text-white/85 transition-colors duration-300">
                      {item.text}
                    </p>
                  </div>

                  {/* Subtle glow on hover */}
                  <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                    style={{
                      boxShadow: "inset 0 0 30px rgba(95, 143, 132, 0.05)"
                    }}
                  />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Open Positions Section - Notus Style
const PositionsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  // Group jobs by category
  const groupedJobs = jobs.reduce((acc, job) => {
    if (!acc[job.category]) {
      acc[job.category] = [];
    }
    acc[job.category].push(job);
    return acc;
  }, {} as Record<string, typeof jobs>);

  // Category badge colors
  const categoryColors: Record<string, string> = {
    Engineering: "text-orange-400",
    Operations: "text-blue-400",
    Sales: "text-purple-400",
    Marketing: "text-pink-400"
  };

  return (
    <section id="positions" ref={ref} className="relative py-24 md:py-32">
      <div className="container px-4 relative z-10 max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-orange-400 text-xs font-medium tracking-wider uppercase mb-4 block">
            Open Roles
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white leading-tight">
            Checkout Our Open Roles
          </h2>
        </motion.div>

        {/* Grouped Job Listings */}
        <div className="space-y-0">
          {Object.entries(groupedJobs).map(([category, categoryJobs], categoryIndex) => (
            <motion.div
              key={category}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: categoryIndex * 0.1 }}
            >
              {/* Category Header with Badge */}
              <div className="flex items-center gap-3 py-4">
                <span className={`inline-flex items-center px-3 py-1 rounded-md bg-white/5 border border-white/10 text-xs font-medium ${categoryColors[category] || 'text-white'}`}>
                  {category}
                </span>
              </div>

              {/* Job Listings */}
              <div className="border-t border-white/[0.08]">
                {categoryJobs.map((job, jobIndex) => (
                  <Link
                    key={job.id}
                    to={job.detailUrl}
                    className="group flex items-center justify-between py-5 border-b border-white/[0.08] hover:bg-white/[0.02] transition-colors duration-300 px-2 -mx-2"
                  >
                    <div className="flex items-center gap-3">
                      {/* Colored dot */}
                      <motion.span
                        className={`w-1.5 h-1.5 rounded-full ${category === 'Engineering' ? 'bg-orange-400' : category === 'Sales' ? 'bg-purple-400' : category === 'Operations' ? 'bg-blue-400' : 'bg-pink-400'}`}
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 2, repeat: Infinity, delay: jobIndex * 0.3 }}
                      />
                      <h3 className="text-white font-medium group-hover:text-emerald-400 transition-colors">
                        {job.title}
                      </h3>
                    </div>

                    <div className="flex items-center gap-6">
                      {/* Location & Type */}
                      <span className="text-sm text-zinc-500 hidden sm:block">
                        {job.location} / {job.type}
                      </span>

                      {/* Arrow */}
                      <motion.div
                        className="text-zinc-500 group-hover:text-emerald-400 transition-colors"
                        whileHover={{ x: 4 }}
                      >
                        <ArrowRight className="h-4 w-4" />
                      </motion.div>
                    </div>
                  </Link>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Benefits Section - Bento Grid Design
const BenefitsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  // Animated card wrapper component
  const AnimatedCard = ({
    children,
    className = "",
    delay = 0,
    tall = false
  }: {
    children: React.ReactNode;
    className?: string;
    delay?: number;
    tall?: boolean;
  }) => (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6, delay: delay * 0.1, ease: "easeOut" }}
      whileHover={{ y: -6, scale: 1.02 }}
      className={`group relative overflow-hidden rounded-3xl bg-zinc-900/60 border border-white/[0.08] backdrop-blur-sm transition-all duration-500 ${tall ? 'row-span-2' : ''} ${className}`}
      style={{ minHeight: tall ? '424px' : '200px' }}
    >
      {/* Animated border glow on hover */}
      <div className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-all duration-500"
        style={{
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), transparent, rgba(20, 184, 166, 0.1))',
          boxShadow: 'inset 0 0 60px rgba(16, 185, 129, 0.05)'
        }}
      />

      {/* Shimmer effect on hover */}
      <motion.div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 pointer-events-none"
        style={{
          background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.03) 50%, transparent 60%)',
          backgroundSize: '200% 100%'
        }}
        animate={{
          backgroundPosition: ['200% 0', '-200% 0']
        }}
        transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
      />

      {/* Content */}
      <div className="relative z-10 h-full">
        {children}
      </div>

      {/* Corner accent */}
      <div className="absolute bottom-0 right-0 w-24 h-24 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
        <div className="absolute bottom-0 right-0 w-full h-full bg-gradient-to-tl from-emerald-500/10 to-transparent rounded-tl-[100px]" />
      </div>
    </motion.div>
  );

  // Animated floating icon component
  const FloatingIcon = ({ Icon, color = "emerald" }: { Icon: any; color?: string }) => (
    <motion.div
      className={`w-14 h-14 rounded-2xl bg-gradient-to-br from-${color}-500/20 to-${color}-500/5 border border-${color}-500/20 flex items-center justify-center group-hover:border-${color}-400/40 transition-all duration-300`}
      animate={{ y: [0, -4, 0] }}
      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      style={{
        background: `linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(16, 185, 129, 0.05))`,
        borderColor: 'rgba(16, 185, 129, 0.2)'
      }}
    >
      <Icon className="h-6 w-6 text-emerald-400" />
    </motion.div>
  );

  return (
    <section ref={ref} className="relative py-24 md:py-32 overflow-hidden">
      <div className="container px-4 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-emerald-400 text-sm font-medium tracking-wider uppercase mb-4 block">
            WHY AYRA
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white max-w-3xl mx-auto leading-tight">
            We take care of our team so they can take care of our{" "}
            <span className="text-emerald-400">product</span>.
          </h2>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-6xl mx-auto auto-rows-[200px]">

          {/* Card 1: Remote-first (Tall - 2 rows) */}
          <AnimatedCard tall delay={1}>
            <div className="flex flex-col h-full p-8">
              <FloatingIcon Icon={Laptop} />

              <div className="mt-auto">
                <h3 className="text-xl font-semibold text-white mb-3">Remote-first</h3>
                <p className="text-white/50 text-sm leading-relaxed">
                  Work from anywhere. We're distributed across the globe and async-friendly. Your home is your office.
                </p>
              </div>

              {/* Decorative world map dots */}
              <div className="absolute top-20 right-6 w-32 h-20 opacity-20 group-hover:opacity-40 transition-opacity duration-500">
                <div className="absolute w-2 h-2 bg-emerald-400 rounded-full animate-pulse" style={{ top: '20%', left: '30%' }} />
                <div className="absolute w-1.5 h-1.5 bg-emerald-400/80 rounded-full animate-pulse" style={{ top: '50%', left: '60%', animationDelay: '0.5s' }} />
                <div className="absolute w-2 h-2 bg-emerald-400 rounded-full animate-pulse" style={{ top: '40%', left: '10%', animationDelay: '1s' }} />
                <div className="absolute w-1.5 h-1.5 bg-emerald-400/70 rounded-full animate-pulse" style={{ top: '70%', left: '45%', animationDelay: '1.5s' }} />
                <div className="absolute w-1 h-1 bg-emerald-400/60 rounded-full animate-pulse" style={{ top: '30%', left: '80%', animationDelay: '2s' }} />
              </div>
            </div>
          </AnimatedCard>

          {/* Card 2: Equity */}
          <AnimatedCard delay={2}>
            <div className="flex flex-col h-full p-7">
              <FloatingIcon Icon={TrendingUp} />
              <div className="mt-auto">
                <h3 className="text-lg font-semibold text-white mb-2">Equity</h3>
                <p className="text-white/50 text-sm">Real ownership in a company that's actually growing.</p>
              </div>

              {/* Animated growth chart */}
              <div className="absolute bottom-6 right-6 flex items-end gap-1 opacity-30 group-hover:opacity-60 transition-opacity">
                {[40, 55, 45, 70, 60, 85].map((h, i) => (
                  <motion.div
                    key={i}
                    className="w-2 bg-emerald-500/50 rounded-t"
                    initial={{ height: 0 }}
                    animate={isInView ? { height: h * 0.4 } : { height: 0 }}
                    transition={{ duration: 0.5, delay: 0.5 + i * 0.1 }}
                  />
                ))}
              </div>
            </div>
          </AnimatedCard>

          {/* Card 3: Flexible PTO */}
          <AnimatedCard delay={3}>
            <div className="flex flex-col h-full p-7">
              <FloatingIcon Icon={Palmtree} />
              <div className="mt-auto">
                <h3 className="text-lg font-semibold text-white mb-2">Flexible PTO</h3>
                <p className="text-white/50 text-sm">Take the time you need. We trust you.</p>
              </div>

              {/* Calendar dots */}
              <div className="absolute top-16 right-6 grid grid-cols-5 gap-1.5 opacity-20 group-hover:opacity-40 transition-opacity">
                {Array(15).fill(0).map((_, i) => (
                  <div key={i} className={`w-2 h-2 rounded-full ${i === 7 || i === 12 ? 'bg-emerald-400' : 'bg-white/30'}`} />
                ))}
              </div>
            </div>
          </AnimatedCard>

          {/* Card 4: Learning budget */}
          <AnimatedCard delay={4}>
            <div className="flex flex-col h-full p-7">
              <FloatingIcon Icon={GraduationCap} />
              <div className="mt-auto">
                <h3 className="text-lg font-semibold text-white mb-2">Learning budget</h3>
                <p className="text-white/50 text-sm">$1,000/year for courses, books, conferences.</p>
              </div>

              {/* $1K badge */}
              <motion.div
                className="absolute top-6 right-6 text-3xl font-bold text-emerald-400/20 group-hover:text-emerald-400/40 transition-colors"
                animate={{ scale: [1, 1.02, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                $1K
              </motion.div>
            </div>
          </AnimatedCard>

          {/* Card 5: Best tools */}
          <AnimatedCard delay={5}>
            <div className="flex flex-col h-full p-7">
              <FloatingIcon Icon={Wrench} />
              <div className="mt-auto">
                <h3 className="text-lg font-semibold text-white mb-2">Best tools</h3>
                <p className="text-white/50 text-sm">MacBook, software, gear — whatever you need.</p>
              </div>

              {/* Tool icons */}
              <div className="absolute top-6 right-6 flex items-center gap-2 opacity-30 group-hover:opacity-60 transition-opacity">
                <div className="w-6 h-6 rounded bg-white/10 flex items-center justify-center text-xs">⌘</div>
                <div className="w-6 h-6 rounded bg-white/10 flex items-center justify-center text-xs">⌥</div>
              </div>
            </div>
          </AnimatedCard>

          {/* Card 6: High impact (Wide - 3 cols full width) */}
          <AnimatedCard delay={6} className="md:col-span-3">
            <div className="flex flex-col md:flex-row md:items-center h-full p-8 gap-6">
              <div className="flex-1">
                <FloatingIcon Icon={Rocket} />
                <h3 className="text-xl font-semibold text-white mb-3 mt-6 md:mt-8">High impact</h3>
                <p className="text-white/50 text-sm leading-relaxed max-w-md">
                  Small team = your work actually matters. Ship things that real people use every single day.
                </p>
              </div>

              {/* Animated impact visualization */}
              <div className="hidden md:flex items-center justify-center">
                <motion.div
                  className="relative w-32 h-32"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                >
                  <div className="absolute inset-0 rounded-full border border-emerald-500/20" />
                  <div className="absolute inset-4 rounded-full border border-emerald-500/30" />
                  <div className="absolute inset-8 rounded-full border border-emerald-500/40" />
                  <motion.div
                    className="absolute w-3 h-3 bg-emerald-400 rounded-full shadow-lg shadow-emerald-500/50"
                    style={{ top: '50%', left: '50%', marginTop: -6, marginLeft: -6 }}
                    animate={{ scale: [1, 1.3, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                </motion.div>
              </div>
            </div>
          </AnimatedCard>

        </div>
      </div>
    </section>
  );
};

// Final CTA Section
const FinalCTASection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} className="relative py-24 md:py-32">
      <div className="container px-4 relative z-10">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="text-center max-w-2xl mx-auto"
        >
          <motion.h2
            variants={staggerItem}
            className="headline text-3xl md:text-4xl lg:text-5xl text-white mb-6"
          >
            Don't see your role?{" "}
            <span className="text-primary">Reach out anyway.</span>
          </motion.h2>

          <motion.p
            variants={staggerItem}
            className="body-text text-lg text-white/50 mb-10"
          >
            We're always looking for exceptional people. If you think you'd be a
            great fit, send us a note.
          </motion.p>

          <motion.div
            variants={staggerItem}
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.98 }}
          >
            <Button
              asChild
              size="lg"
              className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-500/20 px-8 py-6 text-base font-medium group"
            >
              <a href="mailto:careers@ayra.cx">
                Get in Touch
                <ExternalLink className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Main Careers Page
const CareersPage = () => {
  return (
    <div className="min-h-screen bg-black relative">

      <Header />
      <main className="relative z-[2]">
        <HeroSection />
        <CultureSection />
        <PositionsSection />
        <BenefitsSection />
        <FinalCTASection />
      </main>
      <Footer />
    </div>
  );
};

export default CareersPage;
